import { Actions as AUTHENTICATION_ACTIONS } from "@/redux/actions/authenticationActions";
import { Actions as COMPANY_ACTIONS } from "@/redux/actions/companyActions";
import { Actions as EMPLOYEEACTIONS } from "@/redux/actions/employeeActions";
import { Actions as GROUPACTIONS } from "@/redux/actions/groupActions";
import { ArrowPathIcon, CheckCircleIcon, PlusIcon } from "@heroicons/react/20/solid";
import cleanDeep from "clean-deep";
import { format } from "date-fns";
import { merge, setWith } from "lodash";
import { useEffect, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import { FileUploader } from "react-drag-drop-files";
import { NotificationManager } from "react-notifications";
import { connect } from "react-redux";
import Select from "react-select";
import { domain } from "../../../store/actions/apiURL";
import AddressLookUp from "../../common/AddressLookUp";
import { DateInputFormat } from "../../common/DateComponents";
import Spinner from "../../common/Spinner";
import { ImagePlusIcon } from "../../common/icons/Solid";
import GatewayPasswordModal from "./uk/GatewayPasswordModal";
import CompanyDetailsFields from "./sections/CompanyDetailsFields";

const mfaOptions = [
	{ label: "Admins & Managers", value: 1 },
	{ label: "All Employees & Admins", value: 2 },
];

const General = ({
	handleLogoUpload,
	updateCompanyDetails,
	currentOrg,
	settings,
	saving,
	disabled,
	setHMRCCredentialsModalStatus,
	isAdmin,
	jurisdictionOptions,
	jurisdiction,
	defaultPageOptions,
}) => {
	const [data, setData] = useState({});
	const updateField = (field, value) => {
		if (!field) return;
		if (field.includes("[")) field = setWith({}, field, value, Object);
		const newData = merge(data, { ...(typeof field == "object" ? field : { [field]: value }) });
		// console.debug(field, value)

		setData({ ...newData });
	};

	const [image, setImage] = useState(settings?.image);
	const [noLongerEmployer, setNoLongerEmployer] = useState(settings?.noLongerEmployer || false);
	const [dateOfEndEmployer, setDateOfEndEmployer] = useState(settings?.dateOfEndEmployer || "");

	const onSubmit = (e) => {
		if (noLongerEmployer && !dateOfEndEmployer)
			return NotificationManager.error("Please select a date of end", "Stop being an employer");
		e.preventDefault();
		e.stopPropagation();
		updateCompanyDetails(
			cleanDeep(
				{
					org: String(currentOrg),
					...data,
					mfaRequirement: data.mfaRequirement ? data.mfaRequirement : 0,
					noLongerEmployer,
					dateOfEndEmployer,
				},
				{
					emptyStrings: false,
					emptyArrays: false,
					emptyObjects: false,
				},
			),
		);
	};

	const handlePhotoUpload = (file) => {
		setImage(URL.createObjectURL(file));
		handleLogoUpload(file, currentOrg);
	};

	useEffect(() => {
		setImage(settings.image);
	}, [settings.image]);

	useEffect(() => {
		if (!settings.org) return;

		setData({
			name: settings?.name,
			peNumber: settings?.peNumber,
			industry: settings?.industry,
			jurisdiction: settings?.jurisdiction,
			phone: settings?.phone,
			aoReference: settings?.aoReference,
			vatNumber: settings?.vatNumber,
			taxNumber: settings?.taxNumber,
			taxOfficeNo: settings?.taxOfficeNo,
			payeRef: settings?.payeRef,
			companyNumber: settings?.companyNumber,
			jobsplusNumber: settings?.jobsplusNumber,
			address: settings?.address,
			billingEmail: settings?.billingEmail,
			principal: settings?.principal,
			principalRole: settings?.principalRole,
			hmrcCredentials: settings?.hmrcCredentials,
			onboarding: settings?.onboarding,
		});
		setNoLongerEmployer(settings.noLongerEmployer || false);
		setDateOfEndEmployer(settings.dateOfEndEmployer || "");
	}, [settings?.org, jurisdictionOptions?.length]);

	return (
		<>
			<GatewayPasswordModal />
			<form
				className="grid xl:grid-cols-3 grid-cols-1 px-8 gap-8"
				onSubmit={onSubmit}
			>
				<div className="form-section xl:col-span-2 xl:row-span-2 my-0">
					<div className="form-container col-xs-12">
						<h4>Company Details</h4>
					</div>
					<CompanyDetailsFields
						data={data}
						updateField={updateField}
						jurisdiction={data?.jurisdiction}
						disabled={disabled}
					/>
					<div className="clearfix" />
				</div>
				<div className="form-section my-0 xl:col-span-1">
					<div className="col-xs-12 form-container">
						<h4>Company Logo</h4>
					</div>
					<div className="col-xs-12 text-right form-container">
						<div className="photo-upload">
							<div
								className="img-container"
								style={{ textAlign: "center" }}
							>
								<FileUploader
									handleChange={handlePhotoUpload}
									name="file"
									children={
										<div className="upload-company-logo">
											<ImagePlusIcon style={{ height: "3.5rem" }} />
											<span>Upload or drop a file</span>
										</div>
									}
									disabled={disabled}
									types={["JPG", "PNG", "JPEG"]}
								/>
								{image && (
									<img
										style={{ maxWidth: "150px", marginTop: "10px", borderRadius: "1rem" }}
										src={image}
									/>
								)}
							</div>
						</div>
					</div>
					<div className="clearfix" />
				</div>
				<div className={`form-section xl:col-span-1 xl:row-span-${jurisdiction === "MT" ? "1" : "2"} my-0`}>
					<div className="form-container pl-8">
						<div className="col-xs-12 px-0">
							<h4>Company Address</h4>
							<AddressLookUp
								updateInput={(input) => {
									updateField("address", {
										...input,
										number: input?.houseNumber,
										houseNumber: undefined,
										postalCode: input?.postCode,
										postCode: undefined,
									});
								}}
								disabled={disabled}
							/>
						</div>
						{["UK"].includes(data?.jurisdiction) ? (
							<>
								<div className="col-lg-6 col-xs-12 px-0">
									<div className="form-material form-container">
										<input
											className="form-control form-input"
											id="address[subBuilding]"
											name="address[subBuilding]"
											value={data?.address?.subBuilding}
											onChange={(e) => {
												updateField(e.target.name, e.target.value);
											}}
											autoComplete="off"
											disabled={disabled}
										/>
										<label htmlFor="address[subBuilding]">Sub Building</label>
									</div>
								</div>
							</>
						) : null}

						<div className="col-lg-6 col-xs-12 px-0">
							<div className="form-material form-container">
								<input
									className="form-control form-input"
									id="address[number]"
									name="address[number]"
									value={data?.address?.number}
									onChange={(e) => {
										updateField(e.target.name, e.target.value);
									}}
									autoComplete="off"
									disabled={disabled}
								/>
								<label htmlFor="address[number]">Building</label>
							</div>
						</div>

						<div className={`col-lg-${["UK"].includes(data?.jurisdiction) ? "12" : "6"} col-xs-12 px-0`}>
							<div className="form-material form-container">
								<input
									className="form-control form-input"
									id="address[street]"
									name="address[street]"
									value={data?.address?.street}
									onChange={(e) => {
										updateField(e.target.name, e.target.value);
									}}
									autoComplete="off"
									disabled={disabled}
								/>
								<label htmlFor="address[street]">Street</label>
							</div>
						</div>

						<div className="col-lg-6 col-xs-12 px-0">
							<div className="form-material form-container">
								<input
									className="form-control form-input"
									id="address[city]"
									name="address[city]"
									value={data?.address?.city}
									onChange={(e) => {
										updateField(e.target.name, e.target.value);
									}}
									autoComplete="off"
									disabled={disabled}
								/>
								<label htmlFor="address[city]">City</label>
							</div>
						</div>

						{["UK"].includes(data?.jurisdiction) ? (
							<>
								<div className="col-lg-6 col-xs-12 px-0">
									<div className="form-material form-container">
										<input
											className="form-control form-input"
											id="address[locality]"
											name="address[locality]"
											value={data?.address?.locality}
											onChange={(e) => {
												updateField(e.target.name, e.target.value);
											}}
											autoComplete="off"
											disabled={disabled}
										/>
										<label htmlFor="address[locality]">Locality</label>
									</div>
								</div>
								<div className="col-lg-4 col-xs-12 px-0">
									<div className="form-material form-container">
										<input
											className="form-control form-input"
											id="address[county]"
											name="address[county]"
											value={data?.address?.county}
											onChange={(e) => {
												updateField(e.target.name, e.target.value);
											}}
											autoComplete="off"
											disabled={disabled}
										/>
										<label htmlFor="address[county]">County</label>
									</div>
								</div>
								<div className="col-lg-4 col-xs-12 px-0">
									<div className="form-material form-container">
										<input
											className="form-control form-input"
											id="address[country]"
											name="address[country]"
											value={data?.address?.country}
											onChange={(e) => {
												updateField(e.target.name, e.target.value);
											}}
											autoComplete="off"
											disabled={disabled}
										/>
										<label htmlFor="address[country]">Country</label>
									</div>
								</div>
							</>
						) : null}
					</div>
					<div className="clearfix" />
				</div>
				{jurisdiction == "UK" && (
					<div className="form-section xl:col-span-2 xl:row-span-1 my-0">
						<div className="form-container col-xs-12">
							<h4>Principal Details</h4>
						</div>
						<div className="form-container col-xs-12">
							<div className="col-xs-3 pl-0">
								<div className="form-material form-container">
									<input
										className="form-control form-input"
										id="principal[title]"
										name="principal[title]"
										value={data?.principal?.title}
										onChange={(e) => {
											updateField(e.target.name, e.target.value);
										}}
										autoComplete="off"
										disabled={disabled}
									/>
									<label htmlFor="principal[title]">Title</label>
								</div>
							</div>

							<div className="col-xs-3 pl-0">
								<div className="form-material form-container">
									<input
										className="form-control form-input"
										id="principal[givenName]"
										name="principal[givenName]"
										value={data?.principal?.givenName}
										onChange={(e) => {
											updateField(e.target.name, e.target.value);
										}}
										autoComplete="off"
										disabled={disabled}
									/>
									<label htmlFor="principal[givenName]">First Name</label>
								</div>
							</div>

							<div className="col-xs-3 pl-0">
								<div className="form-material form-container">
									<input
										className="form-control form-input"
										id="principal[familyName]"
										name="principal[familyName]"
										value={data?.principal?.familyName}
										onChange={(e) => {
											updateField(e.target.name, e.target.value);
										}}
										autoComplete="off"
										disabled={disabled}
									/>
									<label htmlFor="principal[familyName]">Last Name</label>
								</div>
							</div>

							<div className="col-xs-3 pl-0">
								<div className="form-material form-container">
									<input
										className="form-control form-input"
										id="principal[email]"
										name="principal[email]"
										type="email"
										value={data?.principal?.email}
										onChange={(e) => {
											updateField(e.target.name, e.target.value);
										}}
										autoComplete="off"
										disabled={disabled}
									/>
									<label htmlFor="principal[email]">Email</label>
								</div>
							</div>

							<div className="col-xs-3 pl-0">
								<div className="form-material form-container">
									<input
										className="form-control form-input"
										id="principal[phone]"
										name="principal[phone]"
										value={data?.principal?.phone}
										onChange={(e) => {
											updateField(e.target.name, e.target.value);
										}}
										autoComplete="off"
										disabled={disabled}
									/>
									<label htmlFor="principal[phone]">Telephone Number</label>
								</div>
							</div>

							<div className="col-xs-3 pl-0">
								<div className="form-material form-container">
									<input
										className="form-control form-input"
										id="principal[extension]"
										name="principal[extension]"
										value={data?.principal?.extension}
										onChange={(e) => {
											updateField(e.target.name, e.target.value);
										}}
										autoComplete="off"
										disabled={disabled}
									/>
									<label htmlFor="principal[extension]">Extension Code</label>
								</div>
							</div>

							<div className="col-xs-3 pl-0">
								<div className="form-material form-container">
									<input
										className="form-control form-input"
										id="principal[fax]"
										name="principal[fax]"
										value={data?.principal?.fax}
										onChange={(e) => {
											updateField(e.target.name, e.target.value);
										}}
										autoComplete="off"
										disabled={disabled}
									/>
									<label htmlFor="principal[fax]">Fax Number</label>
								</div>
							</div>

							<div className="col-xs-3 pl-0">
								<div className="form-material form-container">
									<input
										className="form-control form-input"
										id="principal[faxExtension]"
										name="principal[faxExtension]"
										value={data?.principal?.faxExtension}
										onChange={(e) => {
											updateField(e.target.name, e.target.value);
										}}
										autoComplete="off"
										disabled={disabled}
									/>
									<label htmlFor="principal[faxExtension]">Fax Extension Code</label>
								</div>
							</div>
						</div>
						<div className="clearfix" />
					</div>
				)}
				<div className="form-section my-0 xl:col-span-2">
					<div className="form-container col-xs-12">
						<h4>Stop being an employer</h4>
					</div>
					<div className="form-container col-xs-12">
						<div className="form-material form-container">
							<div className="col-xs-12 p-0">
								<label className="css-input switch switch-sm switch-success">
									<input
										type="checkbox"
										value={noLongerEmployer}
										checked={noLongerEmployer}
										disabled={disabled}
										onChange={(e) => {
											setNoLongerEmployer(e.target.checked);
											if (e.target.checked == false) setDateOfEndEmployer("");
										}}
									/>
									<span /> This Organisation is no longer an Employer
								</label>
							</div>
							{noLongerEmployer && (
								<div className="col-xs-12 p-0 mt-2">
									<label>Date of End</label>
									<DatePicker
										selected={(dateOfEndEmployer && new Date(dateOfEndEmployer)) || new Date()}
										onChange={(date) => setDateOfEndEmployer(format(date, "yyyy-MM-dd"))}
										dateFormat="dd/MM/yyy"
										popperPlacement="bottom-start"
										customInput={<DateInputFormat />}
									/>
								</div>
							)}
						</div>
					</div>
					<div className="clearfix" />
				</div>

				<div className="form-section xl:row-span-1 my-0">
					<div className="col-xs-12">
						<h4>Payroll Jurisdiction</h4>
					</div>
					<div className="col-xs-12 form-container">
						<div className="form-material">
							<Select
								options={jurisdictionOptions}
								value={jurisdictionOptions.find((i) => i.value == data?.jurisdiction)}
								name="jurisdiction"
								isDisabled={domain !== "local"}
								onChange={(e) => {
									updateField("jurisdiction", e?.value || null);
								}}
							/>
						</div>
					</div>
					<div className="clearfix" />
				</div>
				{data?.jurisdiction === "UK" ? (
					<div className="form-section xl:row-span-1 my-0">
						<div className="form-container col-xs-12">
							<h4>HMRC</h4>
						</div>
						<div className="col-xs-12">
							<div className="form-material form-container">
								<input
									id="hmrcCredentials[senderId]"
									name="hmrcCredentials[senderId]"
									className="form-control form-input"
									disabled={disabled || settings?.isDemo}
									autoComplete="off"
									value={data?.hmrcCredentials?.senderId}
									onChange={(e) => {
										updateField(e.target.name, e.target.value);
									}}
								/>
								<label htmlFor="hmrcCredentials[senderId]">Government Gateway ID</label>
							</div>
						</div>
						<div className="col-xs-12">
							<div className="form-material">
								<label htmlFor="material-text">Government Gateway Password</label>
								Password Status: {data?.hmrcCredentials?.hasPassword ? "Set" : "Not Set"}
							</div>
						</div>
						{!disabled && !settings?.isDemo && (
							<div className="form-container col-xs-12">
								<div
									className="btn btn-success pull-right"
									style={{ marginLeft: 10 }}
									onClick={() => setHMRCCredentialsModalStatus(true)}
								>
									{data?.hmrcCredentials?.hasPassword ? (
										<>
											<ArrowPathIcon style={{ height: "1.5em", float: "left", marginRight: 5 }} />
											<div style={{ float: "left" }}>Update Password</div>
										</>
									) : (
										<>
											<PlusIcon style={{ height: "1.5em", float: "left", marginRight: 5 }} />
											<div style={{ float: "left" }}>Set Password</div>
										</>
									)}
								</div>
							</div>
						)}
						<div className="clearfix" />
					</div>
				) : null}

				<div className="xl:col-span-3">
					{!disabled && (
						<div className="text-right">
							<button
								className="btn btn-success"
								type="submit"
							>
								{saving ? (
									<Spinner
										style={{ height: "1.5em", float: "left", marginRight: 5, float: "left" }}
										className="animate-spin -ml-1 mr-3 h-5 w-5"
									/>
								) : (
									<CheckCircleIcon style={{ height: "1.5em", float: "left", marginRight: 5 }} />
								)}
								<div style={{ float: "left" }}>{saving ? "Saving..." : "Save"}</div>
							</button>
						</div>
					)}
					<div className="clearfix" />
				</div>
				<div className="clearfix" />
			</form>
		</>
	);
};

const mapStateToProps = (state) => {
	return {
		isAdmin: state.my?.profile?.isAdmin,
		profile: state?.my?.profile,
		hasMFA: state?.my?.profile?.hasMFA,
		settings: state.company?.settings,
		currentOrg: state.company?.settings?.org,
		jurisdictionOptions: (state.jurisdictions || [])?.map(({ countryCode, name }) => ({
			label: `${name}/${countryCode}`,
			value: countryCode,
		})),
		disabled:
			state?.permissions?.my?.permissions?.settings == "Read" || state?.company?.settings?.status == "Archived",
		saving: state.company?.saving,
		jurisdiction: state.company?.settings?.jurisdiction,
		companyAdvisor: state.company?.advisor,
		isEnterprise: state.company?.advisor?.bureau === "Enterprise",
		clientCanReview: state.permissions?.plan?.clientReview,
	};
};

const mapDispatchToProps = (dispatch) => ({
	setHMRCCredentialsModalStatus: (status) => dispatch(COMPANY_ACTIONS.setHMRCCredentialsModalStatus(status)),
	employeesGetList: () => dispatch(EMPLOYEEACTIONS.employeesGetList()),
	updateCompanyDetails: (body) => dispatch(COMPANY_ACTIONS.updateCompanyDetails(body)),
	handleLogoUpload: (file, org) => dispatch(COMPANY_ACTIONS.handleLogoUpload({ file, org })),
	setMFAModalStatus: (status) => dispatch(AUTHENTICATION_ACTIONS.setMFAModalStatus(status)),
	getGroups: (options) => dispatch(GROUPACTIONS.getGroups(options)),
});

export default connect(mapStateToProps, mapDispatchToProps)(General);
